import {
  ActionIcon,
  Card,
  Group,
  Popover,
  Stack,
  UnstyledButton,
} from "@mantine/core";

import {
  AttentionIcon,
  ChevronRightIcon,
  HelpIcon,
  Text,
} from "@/components/ui/elements";

import type { MyPageQuery, TrustScoreGrade } from "@/gql/__generated__/graphql";

import { sendTrustScoreHelpOpenEvent } from "@/libs/google";

import {
  NO_EVALUATION_RECORD_DISPLAY_NAME,
  TrustScoreBadge,
} from "./TrustScoreBadge";

/** サーバーが判定した信頼スコア */
export type TrustScore = NonNullable<
  NonNullable<MyPageQuery["usageStatus"]>["trustScore"]
>;

/** ランクに応じたコメント */
const TRUST_SCORE_COMMENTS: Record<TrustScoreGrade, string> = {
  A: "継続的な安定勤務ありがとうございます。現在とても信頼の高い状態です。",
  B: "とても安定した勤務状況です。この調子で勤務を続けていきましょう。",
  C: "安定した勤務を続けると、ゴールド・プラチナへ上がっていきます。",
  D: "直前キャンセルが続いています。勤務を重ねるとランクは戻ります。",
};

/** 評価対象の実績なしのときの補足（ランクごとのコメントの位置に出す） */
const NO_EVALUATION_RECORD_COMMENT =
  "直近3か月に勤務・キャンセルがないため、評価していません";

/** 良い順に並べたランク。最下位の判定に使う（サーバーの TrustScore::GRADES と対応） */
const GRADE_ORDER: TrustScoreGrade[] = ["A", "B", "C", "D"];
const LOWEST_GRADE = GRADE_ORDER[GRADE_ORDER.length - 1];

/** 最下位ランクのときに表示するリスク内容 */
const LOWEST_RANK_RISK_LABEL = "施設からキャンセル依頼を受けるリスクあり";

type Props = {
  trustScore: TrustScore;
  /** キャンセル率・件数の詳細を開く */
  onOpenCancellationStats: () => void;
};

export function TrustScoreCard({ trustScore, onOpenCancellationStats }: Props) {
  // 評価対象の実績なしはランクを持たないため、ランクに紐づく内訳・目安・注意表示を出さない。
  // grade などは旧バージョンのアプリ向けに最上位ランクの値が入っているので参照しない
  const { noEvaluationRecord } = trustScore;
  const comment = noEvaluationRecord
    ? NO_EVALUATION_RECORD_COMMENT
    : (TRUST_SCORE_COMMENTS[trustScore.grade] ?? "");
  const isLowestRank = !noEvaluationRecord && trustScore.grade === LOWEST_GRADE;

  return (
    <Card
      withBorder
      p="sm"
      radius="lg"
      style={{
        backgroundColor: "var(--mantine-color-white)",
        border: "1px solid var(--mantine-color-gray-2)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      }}
    >
      <Stack gap="sm">
        {/* 上部: ランクのバッジと判定の内訳 */}
        <Group gap="sm" align="center" wrap="nowrap">
          {noEvaluationRecord ? (
            <TrustScoreBadge
              noEvaluationRecord
              displayName={NO_EVALUATION_RECORD_DISPLAY_NAME}
            />
          ) : (
            <TrustScoreBadge
              grade={trustScore.grade}
              displayName={trustScore.displayName}
            />
          )}

          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Group gap={2} align="center" wrap="nowrap">
              <Text c="gray.6" fz="xs" fw="500">
                信頼スコア
              </Text>
              <Popover
                width={280}
                position="bottom"
                withArrow
                shadow="md"
                onOpen={sendTrustScoreHelpOpenEvent}
              >
                <Popover.Target>
                  <ActionIcon
                    color="gray"
                    variant="transparent"
                    size="xs"
                    style={{ flexShrink: 0 }}
                    aria-label="信頼スコアの説明"
                  >
                    <HelpIcon size={12} />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown>
                  {/* しきい値はサーバーの app/domains/temporary_job_workers/trust_score.rb
                      の定数、ランク名は config/locales/domains/trust_score/ja.yml が正本。
                      どちらかを変えるときはこの説明文もあわせて直す */}
                  <Stack gap={6}>
                    <Text fz="xs">
                      信頼スコアは、直近3か月の直前キャンセルの「回数」と「率」の2つの基準で判定し、良いほうを採用します。
                    </Text>
                    <Text fz="xs" c="gray.6">
                      回数：0回 プラチナ ／ 1回（勤務とキャンセルの合計5件以上）
                      ゴールド ／ 1回（合計5件未満） シルバー ／ 2回 シルバー ／
                      3回以上 ブロンズ
                    </Text>
                    <Text fz="xs" c="gray.6">
                      率：5%以下 プラチナ ／ 10%以下 ゴールド ／ 15%以下
                      シルバー ／ それ以外 ブロンズ
                    </Text>
                    <Text fz="xs" c="gray.6">
                      直前キャンセルが2回以上あり、率が20%以上のときはブロンズになります。
                    </Text>
                    <Text fz="xs" c="gray.6">
                      直前キャンセルは集計期間（直近3か月）を過ぎると集計から外れます。
                    </Text>
                    <Text fz="xs" c="gray.6">
                      直近3か月に勤務・キャンセルがない場合は、評価対象外になります。
                    </Text>
                  </Stack>
                </Popover.Dropdown>
              </Popover>
            </Group>

            {noEvaluationRecord ? null : (
              <TrustScoreBreakdown trustScore={trustScore} />
            )}
          </Stack>
        </Group>

        {/* 次のランクまでの目安 */}
        {!noEvaluationRecord &&
        trustScore.requiredWorkCountForNextGrade != null &&
        trustScore.nextGradeDisplayName != null ? (
          <Text fz="xs" c="gray.7" fw={500}>
            直前キャンセルなしであと
            {trustScore.requiredWorkCountForNextGrade}回勤務すると
            {trustScore.nextGradeDisplayName}になります（目安）
          </Text>
        ) : null}

        {/* 下部: コメント */}
        {isLowestRank ? (
          <Stack gap={6}>
            <Group
              gap={6}
              align="center"
              wrap="nowrap"
              px="xs"
              py={4}
              style={{
                borderRadius: "var(--mantine-radius-sm)",
                backgroundColor: "var(--mantine-color-violet-0)",
                border: "1px solid var(--mantine-color-violet-3)",
              }}
            >
              <AttentionIcon
                size={13}
                aria-hidden="true"
                style={{
                  color: "var(--mantine-color-violet-6)",
                  flexShrink: 0,
                }}
              />
              <Text fz="xs" fw={700} c="violet.7">
                {LOWEST_RANK_RISK_LABEL}
              </Text>
            </Group>
            {comment !== "" ? (
              <Group
                gap={6}
                align="flex-start"
                wrap="nowrap"
                px="xs"
                py={6}
                style={{
                  borderRadius: "var(--mantine-radius-sm)",
                  backgroundColor: "var(--mantine-color-gray-0)",
                  border: "1px solid var(--mantine-color-gray-2)",
                }}
              >
                <AttentionIcon
                  size={13}
                  aria-hidden="true"
                  style={{
                    color: "var(--mantine-color-red-6)",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                />
                <Text fz="xs" fw={500} c="red.7" flex={1}>
                  {comment}
                </Text>
              </Group>
            ) : null}
          </Stack>
        ) : comment !== "" ? (
          <Text fz="xs" c="dimmed" flex={1}>
            {comment}
          </Text>
        ) : null}
      </Stack>

      <Card.Section withBorder mt="sm">
        <UnstyledButton
          onClick={onOpenCancellationStats}
          w="100%"
          mih={44}
          px="sm"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text fz="sm" fw={700} c="teal.7">
            キャンセル率・件数の詳細を見る
          </Text>
          <ChevronRightIcon
            size={16}
            aria-hidden="true"
            style={{ color: "var(--mantine-color-teal-7)" }}
          />
        </UnstyledButton>
      </Card.Section>
    </Card>
  );
}

type BreakdownProps = {
  trustScore: TrustScore;
};

/** 判定の内訳（直近3か月）。評価対象の実績なしのときは出さない */
function TrustScoreBreakdown({ trustScore }: BreakdownProps) {
  return (
    <>
      <Text fz="xs" c="gray.7">
        直近3か月
      </Text>

      <Text fz="xs" c="gray.7" pl="xs">
        勤務 {trustScore.workedCount}回・キャンセル {trustScore.absenceCount}
        回（うち直前 {trustScore.lateCancellationCount}回）
      </Text>

      {/* キャンセル率と全期間の値は詳細に分け、カードには直近3か月の直前キャンセル率だけを出す */}
      <Group gap={2} align="center" wrap="nowrap" pl="xs">
        <Text fz="xs" c="gray.7">
          直前キャンセル率 {trustScore.rate}%
        </Text>
        <Popover width={280} position="bottom" withArrow shadow="md">
          <Popover.Target>
            <ActionIcon
              color="gray"
              variant="transparent"
              size="xs"
              style={{ flexShrink: 0 }}
              aria-label="直前キャンセル率の説明"
            >
              <HelpIcon size={12} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <Text fz="xs">
              直前キャンセル率は、勤務数とキャンセル件数の合計（判定対象件数）のうち、勤務開始の24時間以内にキャンセルした割合です。全期間の値やキャンセル率は「詳細を見る」から確認できます。
            </Text>
          </Popover.Dropdown>
        </Popover>
      </Group>

      <Text fz="xs" c="gray.6">
        採用した基準：{trustScore.basis}
      </Text>
    </>
  );
}
